// eventStatuses, travelOptions, TravelOption, actRateCategories now live in the shared
// package (single source of truth, also consumed by Cloud Functions). Re-exported here so
// existing `from 'constants/optionLists'` importers keep working.
export { eventStatuses, travelOptions, actRateCategories } from '@otm/shared/event';
export type { EventStatus, TravelOption, ActRateCategory } from '@otm/shared/event';

export const entertainmentTypes = [
  'Acoustic Soul',
  'All Female',
  'AV',
  'Band',
  'Band & Specialty',
  'Beat Suite',
  'Beauté Et Musique',
  'Bluegrass Quartet',
  'Brazilian Samba-Jazz',
  'Choir',
  'Collective',
  'Custom Band',
  'Dance Troupe',
  'Digital',
  'DJ',
  'Ensemble',
  'Five Stone Band',
  'Folk Pop',
  'Gaming',
  'Gospel Choir',
  'Horns',
  'Jazz Quartet',
  'Jazz Trio',
  'Jukebox',
  'Lighting',
  'Mitzvah',
  'Musician',
  'Photo Booths',
  'Pop Classical',
  'Reggae Band',
  'Rocktail Trio',
  'Singer',
  'Soloist',
  'Souled Out',
  'Specialty & Music',
  'Specialty Talent',
  'String Ensemble',
  'Sway',
  'Synergy',
  'Synergy + Specialty',
  'TCB',
  'TCB + DJ',
  'The Brit Pack',
  'TNM',
  'WCC',
  'TCB-SO',
  'TCB-TNM',
  'TCB-COL',
  'Orchestra',
  'ALCHEMY',
  'PROMO',
  'Violin/DJ Duo',
  'Via Paris',
  'Xaxa Ensemble',
  'Green Levels',
  'Headliner + DJ',
  'DJ + Ensemble',
  'Dueling DJs',
  'DJ Rishi',
  'Clare & Co.'
];

export const eventTypes = [
  'Corporate',
  'Mitzvah',
  'Social',
  'Wedding',
  'Showcase',
  'Promo',
  'Rehearsal'
];

export const jobFolders = {
  Corporate: '0ByhRn5Bf9sMpZjBnNkRLdktnOVk',
  Mitzvah: '0ByhRn5Bf9sMpbTBCM3J5TzRMSmM',
  Promo: '17_viBMg11QuHXEpxLfMVYYGSQZGrq9Y8',
  Showcase: '13b3SYz7JIjeh03teOLkV71aeghiM0Ktd',
  Social: '0ByhRn5Bf9sMpMzdDSHZ6TlUzVWs',
  Wedding: '0ByhRn5Bf9sMpQXVNTlVacWJFNTA'
};

export const accountTypes = [
  'Staff',
  'Talent',
  'Client',
  'Bandleader',
  'Musical Director',
  'Sound',
  'Producer',
  'Strings',
  'Horns',
  'Percussion'
] as const;

export type AccountType = (typeof accountTypes)[number];

/**
 * Account types that grant staff-app access (i.e. routed through the staff
 * subdomain and pulled into the global staff picker list). Validated against
 * `accountTypes` via `satisfies` so the two lists cannot drift.
 */
export const STAFF_LIKE_ACCOUNT_TYPES = [
  'Staff',
  'Bandleader',
  'Musical Director',
  'Sound',
  'Producer',
  'Strings',
  'Horns',
  'Percussion'
] as const satisfies readonly AccountType[];

export type StaffLikeAccountType = (typeof STAFF_LIKE_ACCOUNT_TYPES)[number];

/**
 * Subset of staff-like roles that have reduced access (no `Staff`).
 * Used by `authDefinitions.isReducedAccess01` and access-link routing.
 */
export const REDUCED_ACCESS_ACCOUNT_TYPES = [
  'Bandleader',
  'Musical Director',
  'Sound',
  'Producer',
  'Strings',
  'Horns',
  'Percussion'
] as const satisfies readonly AccountType[];

/**
 * Mapping of account types to their corresponding event team field names.
 * Used to filter the events index so users with these roles see only events
 * where they're assigned to the matching team field.
 */
export const TEAM_ROLE_FILTERS: { role: AccountType; eventField: string }[] = [
  { role: 'Bandleader', eventField: 'bandleader' },
  { role: 'Musical Director', eventField: 'musicalDirector' },
  { role: 'Sound', eventField: 'soundEngineer' },
  { role: 'Producer', eventField: 'producer' },
  { role: 'Strings', eventField: 'strings' },
  { role: 'Horns', eventField: 'horns' },
  { role: 'Percussion', eventField: 'percussion' }
];

export const staffDepartments = ['Intern', 'Basic', 'Admin', 'Super Admin'];

export const authorizations = [
  'View Contractor Rates',
  'View Sales Figures',
  'View Expenses',
  'View Deposits',
  'View Bank Details',
  'View Top Secret Financials',
  'AI Mail Reporting',
  'Assistant (read-only)',
  'Set Payment Notifications'
];

export const gigStatuses = [
  'Pre-inquiry',
  'Inquired',
  'Re-inquired',
  'Available',
  'Selected',
  'Pushed',
  'Confirmed',
  'Re-confirm',
  'Unavailable',
  'Not Selected',
  'Payment Approved',
  'Paid'
];

export const gigStatusesWithEmail = [
  'Inquired',
  'Re-inquired',
  'Available',
  'Selected',
  'Pushed',
  'Re-confirm',
  'Payment Approved'
];

export const rateTypes = ['Ceremony', 'Cocktail', 'Dinner', 'Dance', 'Other'];

export const gigRateTypes = [
  'Travel',
  'Cocktail',
  'Ceremony',
  'Overtime',
  'Charts',
  'Gratuity',
  'Referral Fee',
  'Reimbursement',
  'AV Rental',
  'Backline Rental',
  'Prop Costume Rental'
];

export const serviceCategories = [
  'Bands',
  'Unique Ensemble',
  'Feature Performance',
  'Specialty',
  'Photo',
  'Production',
  'Sound',
  'Lighting'
];

export const serviceCategoriesContract = [
  'Entertainment',
  'Audio Package',
  'Lighting',
  'Video Services',
  'Novelties',
  'Labor',
  'Production',
  'Travel',
  'Miscellaneous',
  ''
];

export const roleCategories = ['Band', 'Specialty Talent', 'Sound', ''];

export const expenseCategories = [
  'Talent',
  'Car Service',
  'Food',
  'Wardrobe',
  'Tech',
  'Supplies',
  'Decor/Design',
  'Hair & Makeup Supplies',
  'Photo/Video',
  'Lodge',
  'Virtual- talent segment from vendor',
  'Parking',
  'AIR TRAVEL',
  'Hotel',
  'Rehearsal Space',
  'Music',
  'Fashion Nova',
  'Equipment Rental'
];

/**
 * Fixed name for the daily payment-reminder staff digest. Both the sender
 * (functions/src/payments/reminders) and this subscribe list must use this exact
 * string, so it is intentionally NOT user-editable. The functions package keeps
 * its own literal copy since it compiles separately.
 */
export const PAYMENT_REMINDER_DIGEST_FEED = 'Payment Reminder Digest';

/**
 * Subscription feeds only Admins may toggle in contact Notification Settings.
 * "Client Signed Contract" is admin-only because its email carries a copy of the
 * signed contract (sensitive).
 */
export const ADMIN_ONLY_SUBSCRIPTION_FEEDS = [PAYMENT_REMINDER_DIGEST_FEED, 'Client Signed Contract'];

export const subscriptionFeeds = [
  'Test',
  'History / Task Reminders',
  'Event Booked',
  'Producer Set',
  'Assigned To',
  'Client Portal Edits',
  'Missing Contractor - Two Week Reminder',
  'Missing Details - Two Week Reminder',
  'Missing Deposit - Two Weeks from Booking',
  PAYMENT_REMINDER_DIGEST_FEED,
  'Client Viewed Contract',
  'Client Signed Contract',
  'Client Made Payment',
  'Client Payment Failed',
  'Auto-Pay Invoices Queued'
];

export const gigCategories = ['Music', 'Specialty', 'Audio', 'Visual', 'Lighting'];

/**
 * Forced notification recipients — key event roles that always receive specific
 * notifications on ALL channels (in-app + email) regardless of their personal
 * subscription settings.  This is an escape hatch from the normal opt-in system.
 *
 * - `notification`: must match a value in `subscriptionFeeds`
 * - `role`: human-readable label shown in the UI
 * - `eventField`: the field name on the Event object that holds the contact ID
 * - `description`: shown in the Notification Settings UI
 *
 * To add a new forced recipient:
 *   1. Add an entry here.
 *   2. In the relevant trigger (e.g. PortalQuestions.tsx), pass `event[eventField]?.id`
 *      into the `forcedRecipientIds` argument of `createGroupNotificaiton`.
 */
export const forcedNotificationRecipients: {
  notification: string;
  role: string;
  eventField: string;
  description: string;
}[] = [
  {
    notification: 'Client Portal Edits',
    role: 'Bandleader',
    eventField: 'bandleader',
    description:
      'The assigned bandleader is always copied on all client portal edit notifications, bypassing subscription settings.'
  }
];

